import { useEffect, useRef } from 'react';

interface Wave {
  amplitude: number;
  frequency: number;
  speed: number;
  phase: number;
  color: string;
}

export default function WaveBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const waves: Wave[] = [
      { amplitude: 60,  frequency: 0.010, speed: 0.012, phase: 0,    color: 'rgba(59, 130, 246, 0.35)' },  // blue-500
      { amplitude: 45,  frequency: 0.014, speed: 0.018, phase: 1.5,  color: 'rgba(99, 102, 241, 0.30)' },  // indigo-500
      { amplitude: 50,  frequency: 0.008, speed: 0.010, phase: 3.0,  color: 'rgba(139, 92, 246, 0.25)' },  // violet-500
      { amplitude: 35,  frequency: 0.018, speed: 0.022, phase: 0.8,  color: 'rgba(59, 130, 246, 0.20)' },  // blue lighter
      { amplitude: 55,  frequency: 0.006, speed: 0.008, phase: 4.5,  color: 'rgba(56, 189, 248, 0.18)' },  // sky-400
    ];

    let tick = 0;

    const drawWave = (wave: Wave, verticalOffset: number) => {
      const { amplitude, frequency, speed, phase, color } = wave;
      const w = canvas.width;
      const h = canvas.height;

      ctx.beginPath();
      ctx.moveTo(0, h);

      for (let x = 0; x <= w; x += 2) {
        const y =
          verticalOffset +
          amplitude * Math.sin(x * frequency + phase + tick * speed) +
          amplitude * 0.4 * Math.sin(x * frequency * 1.7 + phase * 1.3 + tick * speed * 0.8);
        ctx.lineTo(x, y);
      }

      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      grad.addColorStop(0, '#0f172a');   // slate-900
      grad.addColorStop(0.5, '#1e1b4b'); // indigo-950
      grad.addColorStop(1, '#0f172a');   // slate-900
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const h = canvas.height;

      // Draw waves from bottom up, slightly staggered vertically
      drawWave(waves[4], h * 0.82);
      drawWave(waves[3], h * 0.74);
      drawWave(waves[2], h * 0.65);
      drawWave(waves[1], h * 0.56);
      drawWave(waves[0], h * 0.48);

      tick++;
      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
